import { Authority } from '../enums/authority';
import { UserDataInterface } from '../interfaces/UserInterface';

export interface User extends UserDataInterface {}

export class User {
  private permissions: Set<Authority>;

  constructor(args: UserDataInterface) {
    Object.assign(this, args);

    this.permissions = new Set(
      args.authorities.map(({ authority }) => authority),
    );
  }

  private firstLetterToUpperCase(payload: string): string {
    return payload.charAt(0).toUpperCase();
  }

  // Duplicating logic from the TenantTileCard config for creating consistent initials from the tenant name.
  // https://github.com/project44/micro-webapps/blob/master/libs/iam-team-ui-utils/src/domains/Tenant/infrastructure/ui/TenantTileCard/TenantTileCard.component.ts#L13
  public getTenantInitials(): string {
    const tenantInitials = this.companyName
      .replace(/[^a-zA-Z]+/g, String(' '))
      .trim()
      .split(' ')
      .filter(Boolean);

    switch (tenantInitials.length) {
      case 0:
        return '-';
      case 1:
        return this.firstLetterToUpperCase(tenantInitials[0]);
      default:
        const firstLetter = this.firstLetterToUpperCase(tenantInitials[0]);
        const secondLetter = this.firstLetterToUpperCase(tenantInitials[1]);

        return `${firstLetter}${secondLetter}`;
    }
  }

  public getFullName(): string {
    return `${this.firstName} ${this.lastName}`;
  }

  public isP44Admin(): boolean {
    return this.hasAnyOfPermissions([Authority.ROLE_P44ADMIN]);
  }

  public isP44User(): boolean {
    //P44 employee username is email@project44.com
    return this.username.toLowerCase().endsWith('@project44.com');
  }

  public hasAnyOfPermissions(permissions: Authority[]): boolean {
    return permissions.some((role) => this.permissions.has(role));
  }

  public hasAllPermissions(permissions: Authority[]): boolean {
    return permissions.every((role) => this.permissions.has(role));
  }

  public static fromJSON(data: UserDataInterface): User {
    return new User(data);
  }
}
